import React, { useMemo } from 'react'
import { graphql } from 'gatsby'
import Image from 'widgets/Image'
import { lazyload } from 'utils/lazyload'
import { withArtDirection } from 'gatsby-plugin-image'
import styled from 'styled-components'

const UIHeroCorner = lazyload(() => import('ui/elements/Hero/Corner'))
const UIHeroPolygon = lazyload(() => import('ui/elements/Hero/Polygon'))

export type LocalizedString = Partial<
  Record<'de' | 'en' | 'localized' | string, string>
>
export type Hero =
  | Queries.HeroFragmentFragment
  | (Omit<Queries.HeroFragmentFragment, '__typename'> & {
      __typename: undefined
      image: Queries.HeroImageFragment
    })

export const HeroPolygon: React.FC<
  {
    hero: Hero
  } & React.ComponentProps<typeof UIHeroPolygon>
> = ({ hero, ...props }) => {
  return <RenderHero component={UIHeroPolygon} hero={hero} heroProps={props} />
}

export const HeroCorner: React.FC<
  {
    hero: Hero
  } & React.ComponentProps<typeof UIHeroCorner>
> = ({ hero, ...props }) => {
  return <RenderHero component={UIHeroCorner} hero={hero} heroProps={props} />
}

const HeroImage = styled(Image)`
  [data-gatsby-image-wrapper] {
    --img-width: 576px;
    --img-height: 300px;

    width: var(--img-width) !important;
    height: var(--img-height) !important;

    @media screen and (min-width: 576px) {
      --img-width: 768px;
    }
    @media screen and (min-width: 769px) {
      --img-width: 1280px;
      --img-height: 600px;
    }
    @media screen and (min-width: 1281px) {
      --img-width: 1920px;
      --img-height: 600px;
    }
    @media screen and (min-width: 1921px) {
      --img-width: 2560px;
      --img-height: 600px;
    }
  }

  [data-placeholder-image] {
    width: 100% !important;
    height: 100% !important;
  }
`

type BaseProps = {
  heading?: string
  subheading?: string | null
  noHeading?: boolean
  slim?: boolean
}
type RenderHeroProps<T extends BaseProps> = React.PropsWithChildren<{
  component: React.FC<T>
  hero: Hero
  heroProps: T
}>

function RenderHero<T extends BaseProps>({
  component: Component,
  hero,
  heroProps,
}: RenderHeroProps<T>): React.ReactElement | null {
  const heroData = useMemo(() => {
    if (!hero) {
      return
    }

    const slim = hero.slim || heroProps.slim

    let images: Record<string, any> | undefined = undefined
    if (hero.image) {
      if (slim) {
        images = {
          xxxl: hero.image.imageCompact_xxl,
          xxl: hero.image.imageCompact_xxl,
          xl: hero.image.imageCompact_xl,
          lg: hero.image.imageCompact_lg,
          md: hero.image.imageCompact_md,
          sm: hero.image.imageCompact_sm,
          xs: hero.image.imageCompact_xs,
        }
      } else {
        images = {
          xxxl: hero.image.imageFull_xxxl,
          xxl: hero.image.imageFull_xxl,
          xl: hero.image.imageFull_xl,
          lg: hero.image.imageFull_lg,
          md: hero.image.imageFull_md,
          sm: hero.image.imageFull_sm,
          xs: hero.image.imageFull_xs,
        }
      }
    }

    let image = undefined
    if (images) {
      image = withArtDirection(images.xs, [
        {
          media: '(min-width: 2561px)',
          image: images.xxxl,
        },
        {
          media: '(min-width: 1921px) and (max-width: 2560px)',
          image: images.xxl,
        },
        {
          media: '(min-width: 1281px) and (max-width: 1920px)',
          image: images.xl,
        },
        {
          media: '(min-width: 769px) and (max-width: 1280px)',
          image: images.lg,
        },
        {
          media: '(min-width: 577px) and (max-width: 768px)',
          image: images.md,
        },
        {
          media: '(min-width: 361px) and (max-width: 576px)',
          image: images.sm,
        },
      ])

      image.placeholder = {
        fallback: hero.image?.asset?.metadata?.lqip ?? undefined,
      }
    }

    const {
      heading: overrideHeading,
      subheading: overrideSubheading,
      slim: _ignore,
      ...overrideRest
    } = heroProps

    const heading = overrideHeading ?? hero?.title?.localized

    return {
      heading,
      subheading:
        overrideSubheading !== undefined
          ? overrideSubheading
          : hero?.subtitle?.localized,
      backgroundColor:
        hero.image?.asset?.metadata?.palette?.lightMuted?.background ??
        undefined,
      placeholder: image?.placeholder?.fallback,
      background: image ? (
        <HeroImage
          asset={image}
          layout="constrained"
          style={{ objectFit: 'cover', objectPosition: 'center center' }}
          alt={hero?.alt?.localized ?? heading ?? ''}
          eager
        />
      ) : undefined,
      slim: slim,
      ...overrideRest,
    }
  }, [hero, heroProps])

  if (!heroData) {
    return null
  }

  return <Component {...(heroData as any) /* TODO: remove "any" */} />
}

export const query = graphql`
  fragment HeroFragment on SanityHero {
    __typename
    title {
      en
      de
      localized
    }
    subtitle {
      en
      de
      localized
    }
    image {
      ...HeroImage
    }
    slim
  }

  fragment HeroImage on SanityExtendedImageLocalized {
    __typename
    asset {
      _id
      metadata {
        lqip
        palette {
          lightMuted {
            background
          }
        }
      }
    }
    crop: _rawCrop
    hotspot: _rawHotspot
    imageFull_xxxl: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 2000
      height: 600
      outputPixelDensities: [1, 1.25, 1.5, 2]
    )
    imageFull_xxl: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 1792
      height: 600
      outputPixelDensities: [1]
    )
    imageFull_xl: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 1344
      height: 600
      outputPixelDensities: [1]
    )
    imageFull_lg: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 896
      height: 600
      outputPixelDensities: [1]
    )
    imageFull_md: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 538
      height: 600
      outputPixelDensities: [1]
    )
    imageFull_sm: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 576
      height: 300
      outputPixelDensities: [1]
    )
    imageFull_xs: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 360
      height: 300
      outputPixelDensities: [1]
    )
    imageCompact_xxl: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 2560
      height: 300
    )
    imageCompact_xl: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 1588
      height: 300
    )
    imageCompact_lg: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 1280
      height: 300
    )
    imageCompact_md: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 768
      height: 300
    )
    imageCompact_sm: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 576
      height: 300
    )
    imageCompact_xs: gatsbyImageData(
      layout: FIXED
      fit: FILL
      width: 360
      height: 300
    )
    alt {
      localized(locale: $language)
    }
  }
`
